import { useEffect, useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import type { Vendor } from "../../types/marketplace.types";
import { useConnectWizardStore } from "../../store/connectWizard.store";
import { formatCadenceLabel } from "../../lib/package-cadence";

interface WizardStep1PackageProps {
  vendor: Vendor;
}

export function WizardStep1Package({ vendor }: WizardStep1PackageProps) {
  const { t } = useTranslation();
  const packageRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedPackageId = useConnectWizardStore((s) => s.selectedPackageId);
  const setPackageId = useConnectWizardStore((s) => s.setPackageId);

  const activePackages = vendor.packages.filter((p) => p.isActive);

  function focusPackage(index: number) {
    packageRefs.current[index]?.focus();
  }

  function handlePackageKeyDown(event: KeyboardEvent<HTMLButtonElement>, pkgId: string, index: number) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      setPackageId(pkgId);
      return;
    }

    const count = activePackages.length;
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      nextIndex = (index + 1) % count;
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      nextIndex = (index - 1 + count) % count;
    }

    if (nextIndex !== null) {
      setPackageId(activePackages[nextIndex]!.id);
      focusPackage(nextIndex);
    }
  }

  // Default to the popular package (or the first) so Continue is reachable
  // immediately, without overriding a choice the user already made.
  useEffect(() => {
    if (selectedPackageId !== null || activePackages.length === 0) return;
    const preselect =
      activePackages.find((p) => p.isPopular) ?? activePackages[0];
    setPackageId(preselect.id);
  }, [selectedPackageId, activePackages, setPackageId]);

  if (activePackages.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-sm text-muted-foreground">
          {t("marketplace.wizard.package.noPackages")}
        </p>
        <ConnectShareList />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
    <div
      role="radiogroup"
      aria-label={t("marketplace.wizard.package.groupLabel")}
      className="grid justify-center gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,280px))]"
    >
      {activePackages.map((pkg, index) => {
        const isSelected = selectedPackageId === pkg.id;
        const cadenceLabel = formatCadenceLabel(pkg.cadence, t);
        const isTabStop =
          isSelected || (selectedPackageId === null && index === 0);
        return (
          <button
            key={pkg.id}
            ref={(el) => {
              packageRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isTabStop ? 0 : -1}
            onClick={() => setPackageId(pkg.id)}
            onKeyDown={(event) => handlePackageKeyDown(event, pkg.id, index)}
            className={cn(
              "group relative flex h-full min-h-[260px] cursor-pointer flex-col gap-4 rounded-lg border p-5 text-left transition-[border-color,box-shadow,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 motion-reduce:transition-none",
              isSelected
                ? "border-primary bg-primary/5 ring-1 ring-primary/20 shadow-[0_6px_20px_-10px] shadow-primary/35"
                : pkg.isPopular
                  ? "border-primary/50 bg-primary/[0.03] ring-1 ring-primary/10 shadow-[0_6px_20px_-10px] shadow-primary/25 hover:border-primary/70 hover:bg-primary/5"
                  : "border-border hover:border-primary/40 hover:bg-muted/40",
            )}
          >
            {pkg.isPopular && (
              <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold leading-none text-primary-foreground shadow-sm shadow-primary/40">
                <Icon name="star" size="xs" color="inherit" className="fill-current" />
                {t("marketplace.wizard.package.popular")}
              </span>
            )}

            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-foreground">{pkg.name}</span>
              <span
                aria-hidden
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 motion-reduce:transition-none",
                  isSelected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border",
                )}
              >
                {isSelected && (
                  <Icon
                    name="check"
                    size="xs"
                    color="inherit"
                    className="animate-in zoom-in-50 duration-200 motion-reduce:animate-none"
                  />
                )}
              </span>
            </div>

            {pkg.price !== null && (
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-semibold tabular-nums text-foreground">
                  {pkg.currency ? `${pkg.currency} ` : ""}
                  {pkg.price.toLocaleString()}
                </span>
                {cadenceLabel && (
                  <span className="text-xs text-muted-foreground">/ {cadenceLabel}</span>
                )}
              </div>
            )}

            {pkg.description && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                {pkg.description}
              </p>
            )}

            <span
              aria-hidden
              className={cn(
                "mt-auto rounded-sm px-3 py-1.5 text-center text-xs font-medium transition-colors duration-200 motion-reduce:transition-none",
                isSelected
                  ? "bg-primary text-primary-foreground"
                  : "border border-border text-foreground group-hover:border-primary/40",
              )}
            >
              {isSelected
                ? t("marketplace.wizard.package.selected")
                : t("marketplace.wizard.package.select")}
            </span>
          </button>
        );
      })}
    </div>
      <ConnectShareList />
    </div>
  );
}

function ConnectShareList() {
  const { t } = useTranslation();
  const items = [
    t("marketplace.wizard.share.package", "The package you choose, used on every order send."),
    t("marketplace.wizard.share.emails", "Partner contact emails receive scripts and revision notes."),
    t("marketplace.wizard.share.terms", "Your signed terms stay on this connection."),
    t("marketplace.wizard.share.answers", "Onboarding answers become the default brief for this partner."),
  ];
  return (
    <section className="mx-auto w-full max-w-[640px] rounded-md border border-border bg-muted/20 px-4 py-3">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {t("marketplace.wizard.share.title", "What the partner receives")}
      </h3>
      <ul className="mt-2 flex flex-col gap-1.5 text-xs leading-relaxed text-muted-foreground">
        {items.map((item) => (
          <li key={item}>• {item}</li>
        ))}
      </ul>
    </section>
  );
}
